import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import {
    BarChart2,
    TrendingUp,
    FileText,
    Download,
    Filter,
    ChevronDown,
    X,
    User,
    Briefcase,
    DollarSign,
    Clock,
    Calendar,
    Search,
    Home,
    MessageCircle,
    Ticket,
    Wallet,
    Settings,
    Star,
    Menu,
    Bell,
    ChevronRight,
    Globe,
    Facebook,
    Twitter,
    Instagram,
    Music,
    User2,
    Settings2,
    Bookmark,
    Shield,
    ChevronLeft,
    ChevronRight as ChevronRightIcon,
} from "lucide-react";
import { MdReviews } from "react-icons/md";
import SharedHeader4 from "../Headers/SharedHeader4";
import SharedFooter3 from "../Footer/SharedFooter3";
import SharedHeader5 from "../Headers/SharedHeader5";
import { getClientReports } from "../redux/Actions/UserActions";
import { RootState, AppDispatch } from "../redux/store";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/Button";
import { getDynamicCurrencySymbol } from "../lib/currencyUtils";
import { useTranslation } from "react-i18next";

const ReportsPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const [searchParams] = useSearchParams();
    const { clientReports, clientReportsLoading, clientReportsError } = useSelector(
        (state: RootState) => state.user
    );
    const { user } = useSelector((state: any) => state.auth);
    
    // Get userId from URL params (for admin viewing specific client) or use current user
    const userId = searchParams.get("userId") || user?.id;
    
    const [activeTab, setActiveTab] = useState("serviceHistory");
    const [showFilters, setShowFilters] = useState(false);
    const [dateRange, setDateRange] = useState("all");
    const [selectedServiceType, setSelectedServiceType] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    // Fetch data when page, filters, or userId change
    useEffect(() => {
        if (userId) {
            dispatch(getClientReports({
                userId,
                page: currentPage,
                limit: itemsPerPage,
                dateRange: dateRange === "all" ? undefined : dateRange,
                serviceType: selectedServiceType === "all" ? undefined : selectedServiceType
            }));
        }
    }, [dispatch, userId, currentPage, itemsPerPage, dateRange, selectedServiceType]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [dateRange, selectedServiceType]);

    // Get data from Redux
    const filteredServiceHistory = clientReports?.serviceHistory || [];
    const totalCount = clientReports?.totalCount || 0;
    const totalPages = clientReports?.totalPages || 1;
    const hasNextPage = clientReports?.hasNextPage || false;

    // Calculate pagination display
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalCount);

    // Get unique service types for the filter dropdown
    const serviceTypes = ["all", ...Array.from(new Set(filteredServiceHistory.map(report => report.service)))];

    // Tab content components
    const ServiceHistoryTab = () => (
        <div className="p-4 bg-white rounded-lg shadow-sm border w-full">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
                <h3 className="text-lg font-semibold">{t("reportsPage.serviceHistory")}</h3>
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="mt-2 sm:mt-0 flex items-center gap-2 px-3 py-1 text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 rounded transition-colors">
                    <Filter className="h-4 w-4" />
                    {showFilters ? t("reportsPage.hideFilters") : t("reportsPage.showFilters")}
                </button>
            </div>

            <AnimatePresence>
                {showFilters && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="mb-4 p-3 bg-gray-50 rounded-lg overflow-hidden">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">{t("reportsPage.dateRange")}</label>
                                <select
                                    value={dateRange}
                                    onChange={e => setDateRange(e.target.value)}
                                    className="w-full p-2 border rounded-lg text-sm">
                                    <option value="all">{t("reportsPage.allTime")}</option>
                                    <option value="30days">{t("reportsPage.last30Days")}</option>
                                    <option value="90days">{t("reportsPage.last90Days")}</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">{t("reportsPage.serviceType")}</label>
                                <select
                                    value={selectedServiceType}
                                    onChange={e => setSelectedServiceType(e.target.value)}
                                    className="w-full p-2 border rounded-lg text-sm">
                                    {serviceTypes.map((type, index) => (
                                        <option key={index} value={type}>
                                            {type === "all" ? t("reportsPage.allServices") : type}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                    <thead>
                        <tr className="border-b">
                            <th className="py-2 px-2 font-medium text-gray-700">{t("reportsPage.service")}</th>
                            <th className="py-2 px-2 font-medium text-gray-700">{t("reportsPage.date")}</th>
                            <th className="py-2 px-2 font-medium text-gray-700 hidden md:table-cell">{t("reportsPage.professional")}</th>
                            <th className="py-2 px-2 font-medium text-gray-700">{t("reportsPage.status")}</th>
                            <th className="py-2 px-2 font-medium text-gray-700">{t("reportsPage.cost")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredServiceHistory.length > 0 ? (
                            filteredServiceHistory.map(report => (
                                <tr key={report.id} className="border-b last:border-0 hover:bg-gray-50">
                                    <td className="py-2 px-2 text-gray-900">{report.service}</td>
                                    <td className="py-2 px-2 text-gray-600">{report.date}</td>
                                    <td className="py-2 px-2 text-gray-600 hidden md:table-cell">
                                        {report.professional}
                                    </td>
                                    <td className="py-2 px-2">
                                        {(() => {
                                            const statusKey = report.status;
                                            let badgeClasses =
                                                "bg-yellow-50 text-yellow-700";

                                            switch (statusKey) {
                                                case "reportsPage.completed":
                                                    badgeClasses =
                                                        "bg-green-50 text-green-700";
                                                    break;
                                                case "reportsPage.inProgress":
                                                    badgeClasses =
                                                        "bg-blue-50 text-blue-700";
                                                    break;
                                                case "reportsPage.cancelled":
                                                    badgeClasses =
                                                        "bg-red-50 text-red-700";
                                                    break;
                                                default:
                                                    badgeClasses =
                                                        "bg-yellow-50 text-yellow-700";
                                            }

                                            return (
                                                <span
                                                    className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${badgeClasses}`}>
                                                    {t(statusKey)}
                                                </span>
                                            );
                                        })()}
                                    </td>
                                    <td className="py-2 px-2 text-gray-900">{getDynamicCurrencySymbol()}{report.cost}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={5} className="py-4 text-center text-gray-500">
                                    {t("reportsPage.noServicesFound")}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="mt-6 pt-4 border-t">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="text-sm text-gray-600">
                            {t("reportsPage.showing", { start: startIndex + 1, end: endIndex, total: totalCount })}
                        </div>
                        
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                            >
                                <ChevronLeft className="h-4 w-4" />
                                <span className="text-sm">{t("reportsPage.previous")}</span>
                            </button>
                            
                            <div className="flex items-center gap-1">
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                                    if (
                                        page === 1 ||
                                        page === totalPages ||
                                        (page >= currentPage - 1 && page <= currentPage + 1)
                                    ) {
                                        return (
                                            <button
                                                key={page}
                                                onClick={() => setCurrentPage(page)}
                                                className={`px-3 py-1 rounded text-sm ${
                                                    currentPage === page
                                                        ? "bg-blue-600 text-white"
                                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                                }`}
                                            >
                                                {page}
                                            </button>
                                        );
                                    } else if (
                                        page === currentPage - 2 ||
                                        page === currentPage + 2
                                    ) {
                                        return <span key={page} className="px-2">...</span>;
                                    }
                                    return null;
                                })}
                            </div>
                            
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                            >
                                <span className="text-sm">{t("reportsPage.next")}</span>
                                <ChevronRightIcon className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* <button className="mt-4 inline-flex items-center px-3 py-1 text-sm font-medium border border-gray-300 rounded hover:bg-gray-100 transition-colors">
                <Download className="h-4 w-4 mr-2" />
                {t("reportsPage.downloadReport")}
            </button> */}
        </div>
    );

    const FinancialOverviewTab = () => (
        <div className="p-4 bg-white rounded-lg shadow-sm border w-full">
            <h3 className="text-lg font-semibold mb-4">{t("reportsPage.financialOverview")}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-3 bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-700">{t("reportsPage.totalSpent")}</p>
                    <p className="text-2xl font-bold text-blue-900">{getDynamicCurrencySymbol()}{clientReports?.financialOverview.totalSpent || 0}</p>
                </div>
                <div className="md:col-span-3 space-y-3">
                    <p className="text-sm font-medium text-gray-700">{t("reportsPage.monthlyBreakdown")}</p>
                    {clientReports?.financialOverview.monthlyBreakdown.map((item, index) => (
                        <div
                            key={index}
                            className="flex justify-between items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <span className="text-sm text-gray-600">{item.month}</span>
                            <span className="text-sm font-medium text-gray-900">{getDynamicCurrencySymbol()}{item.amount}</span>
                        </div>
                    )) || []}
                </div>
            </div>
            {/* <button className="mt-6 inline-flex items-center px-3 py-1 text-sm font-medium border border-gray-300 rounded hover:bg-gray-100 transition-colors">
                <Download className="h-4 w-4 mr-2" />
                {t("reportsPage.downloadFinancialReport")}
            </button> */}
        </div>
    );

    const PerformanceMetricsTab = () => (
        <div className="p-4 bg-white rounded-lg shadow-sm border w-full">
            <h3 className="text-lg font-semibold mb-4">{t("reportsPage.performanceMetrics")}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 text-center">
                    <BarChart2 className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-gray-900">
                        {clientReports?.performanceMetrics.completedServices || 0}
                    </p>
                    <p className="text-sm text-gray-600">{t("reportsPage.completedServices")}</p>
                </div>
                <div className="p-4 rounded-lg bg-gradient-to-br from-yellow-50 to-orange-50 text-center">
                    <Star className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-gray-900">{clientReports?.performanceMetrics.avgRating || 0}</p>
                    <p className="text-sm text-gray-600">{t("reportsPage.averageRating")}</p>
                </div>
                <div className="p-4 rounded-lg bg-gradient-to-br from-green-50 to-teal-50 text-center">
                    <Clock className="h-8 w-8 text-green-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-gray-900">{clientReports?.performanceMetrics.responseTime || "N/A"}</p>
                    <p className="text-sm text-gray-600">{t("reportsPage.avgResponseTime")}</p>
                </div>
            </div>
            {/* <button className="mt-6 inline-flex items-center px-3 py-1 text-sm font-medium border border-gray-300 rounded hover:bg-gray-100 transition-colors">
                <Download className="h-4 w-4 mr-2" />
                {t("reportsPage.downloadPerformanceReport")}
            </button> */}
        </div>
    );

    if (clientReportsLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">{t("reportsPage.loadingReports")}</p>
                </div>
            </div>
        );
    }

    if (clientReportsError || !clientReports) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-600 mb-4">{clientReportsError || t("reportsPage.failedToLoad")}</p>
                    <button 
                        onClick={() => dispatch(getClientReports({
                            userId,
                            page: currentPage,
                            limit: itemsPerPage,
                            dateRange: dateRange === "all" ? undefined : dateRange,
                            serviceType: selectedServiceType === "all" ? undefined : selectedServiceType
                        }))} 
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        {t("reportsPage.retry")}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
            {/* Header with SharedHeader5 */}
            <header className="sticky top-0 z-50 w-full">
                <SharedHeader5 />
            </header>

            {/* Main Content */}
            <div className="flex-1 p-4 lg:p-6 w-full pb-16 sm:pb-6">
                <main className="max-w-5xl mx-auto">
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-gray-900">{t("reportsPage.title")}</h1>
                        <p className="text-sm text-gray-600 mt-1">
                            {t("reportsPage.subtitle")}
                        </p>
                    </div>

                    {/* Tabs - Horizontal on larger screens */}
                    <div className="mb-6 border-b">
                        <div className="flex flex-wrap -mb-px">
                            <button
                                onClick={() => setActiveTab("serviceHistory")}
                                className={`mr-4 inline-flex items-center px-1 py-3 border-b-2 text-sm font-medium ${
                                    activeTab === "serviceHistory"
                                        ? "border-blue-600 text-blue-600"
                                        : "border-transparent text-gray-600 hover:text-blue-600 hover:border-gray-300"
                                }`}>
                                <FileText className="h-4 w-4 mr-2" />
                                {t("reportsPage.serviceHistory")}
                            </button>
                            <button
                                onClick={() => setActiveTab("financialOverview")}
                                className={`mr-4 inline-flex items-center px-1 py-3 border-b-2 text-sm font-medium ${
                                    activeTab === "financialOverview"
                                        ? "border-blue-600 text-blue-600"
                                        : "border-transparent text-gray-600 hover:text-blue-600 hover:border-gray-300"
                                }`}>
                                <DollarSign className="h-4 w-4 mr-2" />
                                {t("reportsPage.financialOverview")}
                            </button>
                            <button
                                onClick={() => setActiveTab("performanceMetrics")}
                                className={`mr-4 inline-flex items-center px-1 py-3 border-b-2 text-sm font-medium ${
                                    activeTab === "performanceMetrics"
                                        ? "border-blue-600 text-blue-600"
                                        : "border-transparent text-gray-600 hover:text-blue-600 hover:border-gray-300"
                                }`}>
                                <TrendingUp className="h-4 w-4 mr-2" />
                                {t("reportsPage.performanceMetrics")}
                            </button>
                        </div>
                    </div>

                    {/* Tab Content */}
                    <AnimatePresence mode="wait">
                        {activeTab === "serviceHistory" && (
                            <motion.div
                                key="serviceHistory"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.2 }}>
                                <ServiceHistoryTab />
                            </motion.div>
                        )}
                        {activeTab === "financialOverview" && (
                            <motion.div
                                key="financialOverview"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.2 }}>
                                <FinancialOverviewTab />
                            </motion.div>
                        )}
                        {activeTab === "performanceMetrics" && (
                            <motion.div
                                key="performanceMetrics"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.2 }}>
                                <PerformanceMetricsTab />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </main>
            </div>
            <br></br>
            <br></br>
            <br></br>
            <br></br>
            <br></br>
            <br></br>

            {/* Footer */}
            <footer className="bg-white border-t w-full mt-auto">
                <div className="max-w-7xl mx-auto">
                    <SharedFooter3 />
                </div>
            </footer>
        </div>
    );
};

export default ReportsPage;
